import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, ChevronRight } from "lucide-react";
import { API } from "@/lib/site";
import ProductCard from "@/components/ProductCard";

/** Shop structure — five groups. Brands map onto the SAME product records the
 *  Tuning Tools section uses (no duplicates): brand tools = category matching
 *  the brand slug; cables & accessories = the shared "accessories" category. */
export const SHOP_GROUPS = [
  { slug: "alientech", name: "Alientech", strap: "KESS3 master & slave tools", logo: "/assets/brands/alientech.webp", tools: ["Tools", "Cables & Accessories"] },
  { slug: "dimsport", name: "Dimsport", strap: "New Genius, New Trasdata & Race EVO", logo: "/assets/brands/dimsport.webp", tools: ["Tools", "Cables & Accessories"] },
  { slug: "magic-motorsport", name: "Magic Motorsport", strap: "FLEX master & slave packages", logo: "/assets/brands/magic-motorsport.webp", tools: ["Tools", "Cables & Accessories"] },
  { slug: "autotuner", name: "AutoTuner", strap: "Master & slave OBD/bench tools", logo: "/assets/brands/autotuner.webp", tools: ["Tools", "Cables & Accessories"] },
  { slug: "diagnostic-tools", name: "Diagnostic Tools", strap: "Autel & TOPDON diagnostics", logo: null, tools: ["Autel", "TOPDON"] },
];

const DIAG_BRANDS = ["Autel", "TOPDON"];

function useProducts() {
  const [products, setProducts] = useState(null);
  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, []);
  return products;
}

function useTitle(title) {
  useEffect(() => {
    document.title = title;
    return () => { document.title = "Regal Tuning"; };
  }, [title]);
}

const PageHead = ({ overline, title, sub }) => (
  <div className="border-b border-line bg-surface">
    <div className="container-regal py-14 sm:py-20">
      <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-regal-amber" data-testid="shop-overline">{overline}</p>
      <h1 className="mt-3 font-display text-3xl sm:text-5xl font-extrabold uppercase leading-[1.05] tracking-tight text-ink" data-testid="shop-title">{title}</h1>
      {sub && <p className="mt-4 max-w-2xl text-sm sm:text-base text-neutral-400 leading-relaxed">{sub}</p>}
    </div>
  </div>
);

export const BrandTile = ({ g }) => (
  <Link to={`/shop/${g.slug}`} data-testid={`shop-tile-${g.slug}`} className="group flex flex-col border border-line bg-white transition-all duration-300 hover:border-regal-amber hover:shadow-[0_16px_40px_rgba(16,24,40,0.10)]">
    <div className="grid aspect-[16/9] place-items-center border-b border-line bg-white p-10">
      {g.logo ? (
        <img src={g.logo} alt={`${g.name} logo`} className="max-h-20 w-auto object-contain transition-transform duration-500 group-hover:scale-105" />
      ) : (
        <div className="text-center">
          <p className="font-display text-2xl font-extrabold uppercase tracking-[0.14em] text-ink">Diagnostic</p>
          <p className="font-display text-2xl font-extrabold uppercase tracking-[0.14em] text-regal-amber">Tools</p>
        </div>
      )}
    </div>
    <div className="flex items-center justify-between p-6">
      <div>
        <p className="font-display text-base font-bold uppercase tracking-wide text-ink group-hover:text-regal-amber transition-colors">{g.name}</p>
        <p className="mt-1 text-xs text-neutral-400">{g.strap}</p>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-500">{g.tools.join("  ·  ")}</p>
      </div>
      <ArrowUpRight size={18} className="shrink-0 text-neutral-400 transition-colors group-hover:text-regal-amber" />
    </div>
  </Link>
);

export default function ShopIndex() {
  useTitle("Shop Tuning Tools, Cables & Accessories | Regal Tuning");
  return (
    <main data-testid="shop-page" className="pt-16 sm:pt-20">
      <PageHead
        overline="The Regal Tuning Shop"
        title="Shop by brand"
        sub="Genuine tuning tools, cables and accessories from the manufacturers we use in our own workshop every day."
      />
      <div className="container-regal py-12 sm:py-16">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {SHOP_GROUPS.map((g) => <BrandTile key={g.slug} g={g} />)}
          <Link to="/shop/all" data-testid="shop-tile-view-all" className="group flex flex-col justify-between border border-regal-amber/40 bg-regal-amber/5 p-6 transition-colors hover:bg-regal-amber/10">
            <p className="font-display text-xl font-extrabold uppercase leading-snug tracking-wide text-ink">Browse the<br />full catalogue</p>
            <p className="mt-8 inline-flex items-center gap-2 font-display text-[12px] font-bold uppercase tracking-[0.16em] text-regal-amber">
              View All Products <ArrowUpRight size={15} />
            </p>
          </Link>
        </div>
      </div>
    </main>
  );
}

export function ShopAll() {
  useTitle("All Products — Tools, Cables & Accessories | Regal Tuning Shop");
  const products = useProducts();
  const grouped = useMemo(() => {
    const order = ["Alientech", "Dimsport", "Magic Motorsport", "AutoTuner", "Regal Tuning", "Used Equipment"];
    const map = {};
    (products || []).forEach((p) => { (map[p.brand || "Other"] = map[p.brand || "Other"] || []).push(p); });
    return order.filter((b) => map[b]).map((b) => [b, map[b]]);
  }, [products]);
  return (
    <main data-testid="shop-all-page" className="pt-16 sm:pt-20">
      <PageHead overline="Shop" title="All products" sub="Every tool, cable and accessory in stock at Regal Tuning — one catalogue, one price list, live stock." />
      <div className="container-regal py-12 sm:py-16">
        {!products ? (
          <div className="h-40 animate-pulse border border-line bg-white" data-testid="shop-all-loading" />
        ) : (
          grouped.map(([brand, items]) => (
            <section key={brand} className="mb-12" data-testid={`shop-all-${brand.toLowerCase().replace(/\s+/g, "-")}`}>
              <h2 className="mb-5 font-display text-lg font-extrabold uppercase tracking-wide text-ink">{brand}</h2>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((p, i) => <ProductCard key={p.slug} p={p} index={i} />)}
              </div>
            </section>
          ))
        )}
      </div>
    </main>
  );
}

export function ShopBrand() {
  const { brand } = useParams();
  const [params, setParams] = useSearchParams();
  const products = useProducts();
  const group = SHOP_GROUPS.find((g) => g.slug === brand);
  const type = params.get("type") || "tools";
  const diagBrand = params.get("brand");
  useTitle(group
    ? (group.slug === "diagnostic-tools"
      ? "Diagnostic Tools — Autel & TOPDON | Regal Tuning Shop"
      : `${group.name} Tools & Accessories | Regal Tuning Shop`)
    : "Shop | Regal Tuning");

  const { tools, cables } = useMemo(() => {
    const all = products || [];
    return {
      tools: all.filter((p) => p.category === brand),
      cables: all.filter((p) => p.category === "accessories"),
    };
  }, [products, brand]);

  if (!group) {
    return (
      <main className="pt-16 sm:pt-20" data-testid="shop-brand-missing">
        <PageHead overline="Shop" title="Brand not found" />
        <div className="container-regal py-16"><Link to="/shop" className="font-mono text-xs uppercase tracking-[0.16em] text-regal-amber">← Back to the shop</Link></div>
      </main>
    );
  }

  if (group.slug === "diagnostic-tools") {
    return (
      <main data-testid="shop-brand-page" className="pt-16 sm:pt-20">
        <PageHead overline="Shop / Diagnostic Tools" title="Diagnostic Tools" sub="Professional diagnostic platforms from Autel and TOPDON." />
        <div className="container-regal py-12 sm:py-16">
          <div className="grid gap-5 sm:grid-cols-2">
            {DIAG_BRANDS.map((b) => (
              <div key={b} data-testid={`diag-${b.toLowerCase()}`} className={`border bg-white p-8 transition-colors ${diagBrand === b.toLowerCase() ? "border-regal-amber" : "border-line"}`}>
                <p className="font-display text-2xl font-extrabold uppercase tracking-[0.1em] text-ink">{b}</p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-regal-amber">Range coming soon</p>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-neutral-400">
                  We're finalising our {b} diagnostic range. Tell us what you need and we'll confirm availability and pricing the same day.
                </p>
                <Link to="/contact" data-testid={`diag-${b.toLowerCase()}-enquire`} className="mt-6 inline-flex items-center gap-2 border border-linebright px-5 py-2.5 font-display text-[11px] font-bold uppercase tracking-[0.14em] text-ink transition-colors hover:border-regal-amber hover:text-regal-amber">
                  Enquire <ArrowUpRight size={13} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </main>
    );
  }

  const tabs = [["tools", "Tools"], ["cables-accessories", "Cables & Accessories"]];
  const shown = type === "cables-accessories" ? cables : tools;
  return (
    <main data-testid="shop-brand-page" className="pt-16 sm:pt-20">
      <PageHead
        overline={`Shop / ${group.name}`}
        title={group.name}
        sub={type === "cables-accessories" ? `${group.name} cables & accessories — plus universal bench accessories used with every tool we sell.` : group.strap + "."}
      />
      <div className="container-regal py-12 sm:py-16">
        <nav className="mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500">
          <Link to="/shop" className="hover:text-regal-amber">Shop</Link>
          <ChevronRight size={12} />
          <span className="text-neutral-300">{group.name}</span>
        </nav>
        <div className="mb-8 flex flex-wrap gap-2" data-testid="shop-type-tabs">
          {tabs.map(([key, label]) => (
            <button key={key} onClick={() => setParams(key === "tools" ? {} : { type: key })} data-testid={`shop-tab-${key}`}
              className={`border px-5 py-2.5 font-display text-[11px] font-bold uppercase tracking-[0.14em] transition-colors ${type === key || (key === "tools" && !params.get("type")) ? "border-regal-amber bg-regal-amber/5 text-regal-amber" : "border-line bg-white text-neutral-400 hover:text-ink"}`}>
              {label}
            </button>
          ))}
        </div>
        {!products ? (
          <div className="h-40 animate-pulse border border-line bg-white" data-testid="shop-brand-loading" />
        ) : shown.length ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((p, i) => <ProductCard key={p.slug} p={p} index={i} />)}
          </div>
        ) : (
          <div className="border border-dashed border-linebright bg-white p-10 text-center" data-testid="shop-brand-empty">
            <p className="font-display text-base font-bold uppercase tracking-wide text-ink">Nothing listed here yet</p>
            <p className="mx-auto mt-2 max-w-md text-sm text-neutral-400">New {group.name} stock lands regularly — ask us and we'll confirm availability for exactly what you need.</p>
            <Link to="/contact" data-testid="shop-brand-enquire" className="mt-5 inline-flex items-center gap-2 border border-linebright px-5 py-2.5 font-display text-[11px] font-bold uppercase tracking-[0.14em] text-ink hover:border-regal-amber hover:text-regal-amber transition-colors">Enquire <ArrowUpRight size={13} /></Link>
          </div>
        )}
      </div>
    </main>
  );
}
